import { BootstrapperConfig } from '@1js/midgard-bootstrapper';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { AWTPiiKind } from '@aria/webjs-sdk';
import { logFeature, logImpression } from '@office-start/telemetry-service';
import { KnownFeatureInstrumentationEvents, KnownFeatureNames, KnownImpressionInstrumentationEvents } from '@office-start/telemetry-types';
import { IPalette } from '@uifabric/styling';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Subscription } from 'rxjs';

import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents, PiiString } from '../instrumentation/instrumentation.service';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { UserContextService } from '../shared-context/user-context.service';
import { AccessTokenConsumer, AccessTokenService } from './access-token.service';
import { LocalAppLauncherService } from './local-app-launcher.service';
import { LPCComplianceEnvironment, PeopleCardConfigService } from './people-card-config.service';
import { Performance } from './performance.service';
import { SchedulePriority, SchedulerService } from './scheduler.service';
import { ThemingService } from './theming.service';
import { LaunchAction, UrlLauncherService } from './url-launcher.service';
import { WindowService } from './window.service';

@Injectable({
    providedIn: 'root'
})
export class PeopleCardService implements OnDestroy {

    private static readonly errorPrefix = 'LPCError: ';

    private readonly darkModeSubscription: Subscription;
    private readonly darkTheme: any;

    private initPromise: Promise<void>;
    private lpc: any;
    private startTime: number;
    private peopleCount: number;
    private isDarkMode: boolean;

    constructor(
        private readonly accessTokenService: AccessTokenService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly contextService: ContextService,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly instrumentationService: InstrumentationService,
        private readonly peopleCardConfigService: PeopleCardConfigService,
        private readonly localAppLauncherService: LocalAppLauncherService,
        private readonly urlLauncherService: UrlLauncherService,
        private readonly windowService: WindowService,
        private readonly scheduler: SchedulerService,
        private readonly ngZone: NgZone,
        private readonly themingService: ThemingService,
        private readonly userContextService: UserContextService
    ) {
        this.peopleCount = 0;

        if (this.capabilitiesService.capabilities.darkModeEnabled) {
            const fontColor = 'rgba(255,255,255,0.75)';
            const darkThemePalette: Partial<IPalette> = {
                themeDarker: fontColor,
                themeDark: fontColor,
                themeDarkAlt: fontColor,
                themePrimary: fontColor, // primary color of texts and icons
                themeSecondary: fontColor, // color on hovering over 'Show More' in content blocks
                themeTertiary: fontColor,
                themeLight: fontColor,
                themeLighter: fontColor,
                themeLighterAlt: fontColor,
                black: '#ffffff',
                blackTranslucent40: 'rgba(255, 255, 255, 0.40)',
                neutralDark: '#ffffff',
                neutralPrimary: '#f8f8f8',
                neutralPrimaryAlt: '#f4f4f4',
                neutralSecondary: '#eaeaea',
                neutralTertiary: '#a6a6a6',
                neutralTertiaryAlt: '#767676',
                neutralQuaternary: '#6b6b6b',
                neutralQuaternaryAlt: '#707070',
                neutralLight: '#666666',
                neutralLighter: '#333333',
                neutralLighterAlt: '#212121',
                accent: fontColor,
                white: '#333333',
                whiteTranslucent40: 'rgba(0, 0, 0, 0.40)'
              };
            this.darkTheme = {
                palette: darkThemePalette
            };
            this.darkModeSubscription = this.themingService.darkModeSubscription.subscribe(event => {
                this.isDarkMode = event;
                if (this.lpc) {
                    this.lpc.loadTheme(this.isDarkMode ? this.darkTheme : undefined);
                }
            });
        }
    }

    public addPersonaCard(element: HTMLElement, upn: string): Promise<void> {
        if (!this.capabilitiesService.capabilities.peopleCardsEnabled) {
            return Promise.reject('People cards disabled');
        }

        if (!this.peopleCardConfigService.config) {
            return Promise.reject('peopleCardConfigService.config is not defined');
        }

        return this.ngZone.runOutsideAngular(() => this.bootstrap().then(() => {
            if (this.lpc) {
                this.lpc.addedPersonaToPageV2(
                    element,
                    {
                        personaInfo: {
                            identifiers: {
                                Smtp: upn,
                                PersonaType: 'User'
                            }
                        },
                        externalAppSessionCorrelationId: this.startupConfigService.config.sharedClientStartupContext.correlationId,
                        behavior: {
                            onCardOpen: () => {
                                const params: InstrumentationParams = {};
                                params[InstrumentationParam.ElementId] = element.id;
                                params[InstrumentationParam.ItemId] = new PiiString(upn, AWTPiiKind.SmtpAddress);
                                this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.PeopleCardImpression, params);
                                logImpression({
                                    eventName: KnownImpressionInstrumentationEvents.PeopleCardImpression,
                                    area: 'PeopleCard',
                                    id: element.id
                                });
                            },
                            onCardClose: null,
                            disableHover: false
                        }
                    }
                );
                this.peopleCount++;
            }
        }));
    }

    public addPersonaCardElement(container: HTMLElement, aadObjectId: string, upn: string = '', openInExtendedView: boolean = true): Promise<void> {
        if (!this.capabilitiesService.capabilities.peopleCardsEnabled) {
            return Promise.reject('People cards disabled');
        }

        if (!this.peopleCardConfigService.config) {
            return Promise.reject('peopleCardConfigService.config is not defined');
        }

        return this.ngZone.runOutsideAngular(() => this.bootstrap().then(() => {
            if (this.lpc) {
                const cardParameters = {
                    cardParameters: {
                        personaInfo: {
                            identifiers: {
                                AadObjectId: aadObjectId,
                                PersonaType: 'User',
                                Smtp: upn
                            }
                        },
                        externalAppSessionCorrelationId: this.startupConfigService.config.sharedClientStartupContext.correlationId,
                        behavior: {
                            onCardOpen: () => {
                                const params: InstrumentationParams = {};
                                params[InstrumentationParam.ElementId] = container.id;
                                params[InstrumentationParam.ItemId] = new PiiString(aadObjectId, AWTPiiKind.Identity);
                                this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.PeopleCardImpression, params);
                                logImpression({
                                    eventName: KnownImpressionInstrumentationEvents.PeopleCardImpression,
                                    area: 'PeopleCard',
                                    id: container.id
                                });
                            },
                            locationToOpen: openInExtendedView ? 'ExpandedView' : 'Card'
                        }
                    }
                };

                ReactDOM.render(
                    React.createElement(this.windowService.nativeWindow.LivePersonaCardHoverTargetV2, cardParameters, React.createElement('div', { id: 'personCardClickElement' })),
                    container
                );
            }
        }));
    }

    public removePersonaCard(element: HTMLElement, upn: string): Promise<void> {
        if (!this.capabilitiesService.capabilities.peopleCardsEnabled) {
            return Promise.reject('People cards disabled');
        }

        if (!this.peopleCardConfigService.config) {
            return Promise.reject('peopleCardConfigService.config is not defined');
        }

        if (!this.initPromise) {
            return Promise.reject('removePersonaCard is called without calling addPersonaCard first');
        }

        return this.ngZone.runOutsideAngular(() => this.initPromise.then(() => {
            if (this.lpc) {
                this.lpc.removedPersonaFromPage(element, upn);
                this.peopleCount--;
            }
        }));
    }

    public bootstrap(): Promise<void> {
        if (!this.initPromise) {
            if (!this.peopleCardConfigService.config) {
                this.initPromise = Promise.reject('peopleCardConfigService.config is not defined');
            } else {
                this.startTime = Performance.now();
                const config: BootstrapperConfig = {
                    actionCallbacks: this.capabilitiesService.capabilities.pwaEnabled ? {
                        openDocument: document => {
                            if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                                import('@office-start/launcher-service').then(
                                    launchSvc => {
                                        launchSvc.launchApp({ action: 'OpenDocument', appName: document.Type, url: document.DownloadUrl})
                                        .then(result => {
                                            if (result.success) {
                                                return;
                                            }
                                            throw new Error('fallback to browser');
                                        }).catch(() => {
                                            launchSvc.launchUrl(document.WebUrl, { action: 'OpenDocFromPeopleCard'});
                                        });
                                    }
                                );
                            } else {
                                this.localAppLauncherService.launchOfficeApp(document.Type, document.DownloadUrl).then(success => {
                                    if (success) {
                                        return;
                                    }
                                    throw new Error('fallback to browser');
                                }).catch(() => {
                                    this.urlLauncherService.openUrl(document.WebUrl, LaunchAction.OpenDocFromPeopleCard);
                                });
                            }
                        },
                        composeMail: recipient => {
                            if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                                import('@office-start/launcher-service').then(
                                    launchSvc => {
                                        launchSvc.launchUrl(`mailto:${recipient}`, {action: 'EmailFromPeopleCard'});
                                    }
                                );
                            } else {
                                this.urlLauncherService.openUrl(`mailto:${recipient}`, LaunchAction.EmailFromPeopleCard);
                            }
                        },
                        startChat: chatObject => {
                            if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                                import('@office-start/launcher-service').then(
                                    launchSvc => {
                                        launchSvc.launchUrl(chatObject.imAddressUrl, { action: 'OpenChatFromPeopleCard' });
                                    }
                                );
                            } else {
                                this.urlLauncherService.openUrl(chatObject.imAddressUrl, LaunchAction.OpenChatFromPeopleCard);
                            }
                        },
                        handleContactNumber: contactNumber => {
                            // TODO: this callback doesn't get triggered from clicking on a phone number, not sure if there's a bug here on the people card team side
                            if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                                import('@office-start/launcher-service').then(
                                    launchSvc => {
                                        launchSvc.launchUrl(`tel:${contactNumber}`, { action: 'HandleNumberFromPeopleCard'} );
                                    }
                                );
                            } else {
                                this.urlLauncherService.openUrl(`tel:${contactNumber}`, LaunchAction.HandleNumberFromPeopleCard);
                            }
                        }
                    } : undefined,
                    culture: this.contextService.sharedContext.officeMarket,
                    region: this.peopleCardConfigService.config.region,
                    clientType: this.peopleCardConfigService.config.clientType,
                    clientContextType: this.peopleCardConfigService.config.clientContextType,
                    clientCorrelationId: this.startupConfigService.config.sharedClientStartupContext.correlationId,
                    getAuthToken: (onSuccess: (token: string, authRequestCorrelationId: string) => void, onFailure: (errorMessage: string, authRequestCorrelationId?: string) => void) => {
                        this.getAuthToken((token, error) => {
                            if (token) {
                                onSuccess(token, this.startupConfigService.config.sharedClientStartupContext.correlationId);
                            } else {
                                onFailure(error, this.startupConfigService.config.sharedClientStartupContext.correlationId);
                            }
                        });
                    },
                    isConsumer: this.startupConfigService.config.authType === AuthType.MSA,
                    complianceEnvironment: this.getLpcEnvironment(),
                    theme: this.isDarkMode ? this.darkTheme : undefined
                };

                const downloadPromise = new Promise<typeof import ('@1js/midgard-bootstrapper/lib/Bootstrapper')>(resolve => {
                    this.scheduler.scheduleWork(() => {
                        this.importBootstrapper().then(resolve);
                    }, SchedulePriority.VeryLow);
                });

                this.initPromise = downloadPromise.then(({ initializeLivePersonaCardAsync }) =>
                    new Promise((resolve, reject) => {
                        this.scheduler.scheduleWork(() => {
                            initializeLivePersonaCardAsync(config).then(() => resolve(null)).catch(reject);
                        }, SchedulePriority.VeryLow);
                    })).then(
                    () => {
                        this.windowService.ensureZoneJs();
                        this.lpc = this.windowService.nativeWindow.LPC;
                        const latency = Performance.now() - this.startTime;
                        this.instrumentInitialization('Start', latency);
                    },
                    (err: string) => {
                        this.windowService.ensureZoneJs();
                        const latency = Performance.now() - this.startTime;
                        this.instrumentInitialization('Error', latency, err);
                    }
                );
            }
        }

        return this.initPromise;
    }

    public population(): number {
        return this.peopleCount;
    }

    public ngOnDestroy() {
        this.darkModeSubscription?.unsubscribe();
    }

    private instrumentInitialization(state: string, latency: number, error?: any) {
        const params = {};
        params[InstrumentationParam.State] = state;
        params[InstrumentationParam.Latency] = Math.ceil(latency).toString();
        if (error) {
            params[InstrumentationParam.Error] = (typeof error === 'string' && error.indexOf(PeopleCardService.errorPrefix) === 0) ? error : '';
        }
        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.PeopleCardInitialization, params);
        logFeature({
            featureName: KnownFeatureNames.PeopleCard,
            eventName: KnownFeatureInstrumentationEvents.PeopleCardInitialization,
            params
        });
    }

    private getAuthToken(callback: (token: string, error: string) => void): void {
        this.accessTokenService.getAccessToken(AccessTokenConsumer.Loki).then((tokenResponse: AccessTokenResponse) => {
            callback(tokenResponse.token, tokenResponse.error ? `Error : ${tokenResponse.error}` : '');
        });
    }

    private importBootstrapper(): Promise<typeof import ('@1js/midgard-bootstrapper/lib/Bootstrapper')> {
        return import(
            /* webpackChunkName: "midgard-bootstrapper" */
            '@1js/midgard-bootstrapper/lib/Bootstrapper'
        );
    }

    private getLpcEnvironment(): LPCComplianceEnvironment {
        const environmentType = this.peopleCardConfigService.config.complianceEnvironment;

        if (this.userContextService.user.isGcc && environmentType === 'Prod') {
            return 'GccModerate';
        }

        return environmentType;
    }
}
